import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { AuthService } from 'src/app/_services/auth/auth.service';
import { LocalStorageService } from 'src/app/_services/localStorage/local-storage.service';
import { LoteService } from 'src/app/_services/lote/lote.service';
import { LoteCabotajeService } from 'src/app/_services/loteCabotaje/lote-cabotaje.service';
import { TonsPerYardService } from 'src/app/_services/tons-per-yard/tons-per-yard.service';
import { Pedimento } from 'src/app/interfaces/abandonosPedimentos.interfaces';
import { BlDetails, ExportBoats } from 'src/app/interfaces/bl.interfaces';
import { CabotageBatch } from 'src/app/interfaces/cabotageBatches.interfaces';
import { ExportOutput, ExportOutputType } from 'src/app/interfaces/exportationBatches.interfaces';
import { LocationInterface, NationalPorts, SalidaCatalogo } from 'src/app/interfaces/lotes.interfaces';
import { TonsPerYard } from 'src/app/interfaces/tons-per-yard.interfaces';
import { AmountsByInputLocationsComponent } from 'src/app/shared/components/amounts-by-input-locations/amounts-by-input-locations.component';

/**
 * Componente que gestiona el formulario de salida por barco de un lote de cabotaje,
 * permitiendo registrar o actualizar salidas asociadas a una embarcación y su destino.
 */
@Component({
  selector: 'app-cabotaje-form-salida-barco',
  templateUrl: './cabotaje-form-salida-barco.component.html',
  styles: [`
  @media (max-width: 950px) {
      .container-grid {
        grid-template-columns: repeat(3, 1fr) !important;
      }
      .empty-container {
        display: none;
      }
      .grid-first {
        grid-column: span 1;
      }
    }
  `]
})
export class CabotajeFormSalidaBarcoComponent implements OnInit {
  @Input() containerStyles = {
    'grid-template-columns': 'repeat(4, 1fr)'
  };
  @Input() movimiento: string = 'crear';
  @Input() outputDetail: ExportOutput[] = [];
  @Input() cabotageBatch: CabotageBatch[] = [];
  @Input() nationalPorts: NationalPorts[] = [];
  @Input() outputTypes: ExportOutputType[] = [];
  @Input() pedimentos: Pedimento[] = [];
  @Input() boats: ExportBoats[] = [];
  @Input() locations: LocationInterface[] = [];
  @Input() amountsPerYardList: TonsPerYard[] = [];
  @Output() onRegresar = new EventEmitter<boolean>();
  @Output() onTipoSalida = new EventEmitter<string>();
  outputForm: FormGroup;
  @Input() tipoTransporte: SalidaCatalogo[] = [];
  @Input() tipoUnidad: SalidaCatalogo[] = [];
  @Input() archivos = {
    ticketBascula: '',
  };
  @Input() blDetails: BlDetails = {
    operation_number:     0,
    boats_name:           '',
    operation_type_number:0,
    bl_tonnage:           0,
    customer_name:        null,
    date_time_capture:    '',
    dock_name:            '',
    port_origin_name:     '',
    start_operations:     '',
    end_operations:       '',
    boat_nationality:     '',
    destination_port:     '',
    operation_id:         0,
    docking_date:         '',
    undocking_date:       '',
    bl_master:            '',
    notice_of_readiness:  '',
  };
  maxSize: boolean = false;
  mensajeError: string = 'Ingrese el archivo';
  overlay: boolean = false;
  mensajeOverlay: string = '';
  iconoOverlay: string = '';
  buttons: boolean = false;
  loading: boolean = false;
  output: ExportOutput = {
    output_type_option_id:  0,
    cabotage_batch_id:      0,
    checker_name:           '',
    destination_id:         0,
    precinct_key:           0,
    departure_date:         '',
    ship_number:            0,
    ship_name:              '',
    total_tonnage:          0,
    total_pieces:           0,
    addressee:              '',
    destination_company:    '',
    request_number:         0,
    customs_agent:          '',
    withdrawal_number:      '',
    entrie_output_file:     '',
    location_list:          []
  }
  batchValue: number = 0;
  id: number = 0;
  shipName: string = '';

  tipoSalida: FormControl = new FormControl('1');

  // Tons and Pieces into Patios
  entryTonnage: number = 0;
  entryPieces: number = 0;
  @ViewChild(AmountsByInputLocationsComponent) amountsComponent!: AmountsByInputLocationsComponent;

  constructor(
    private authService: AuthService,
    private loteService: LoteService,
    private loteCabotajeService: LoteCabotajeService,
    private amountsPerYardSvc: TonsPerYardService,
    private localStorageSvc: LocalStorageService,
    private fb: FormBuilder,
    private router: Router,
  ) {
    const fullName = this.localStorageSvc.getFullName();
    const isCheckerNameDisabled = fullName ? true : false;
    this.outputForm = this.fb.group({
      output_type_option_id: [],
      precinct_key: [201, [Validators.required]],
      checker_name: [{value: fullName, disabled: isCheckerNameDisabled}],
      departure_date:[this.getDate(), [Validators.required]],
      ship_number: ['', [Validators.required]],
      ship_name: [, [Validators.required]],
      total_tonnage: [, [Validators.required, Validators.pattern(/^(?!0\d)\d{0,11}(?:\.\d{1,3})?$/)]],
      total_pieces: [, [Validators.pattern(/^[0-9]+$/)]],
      addressee: [, [Validators.required]],
      destination_company: [, [Validators.required]],
      destination_id: ['', [Validators.required]],
      consignee_name: [],
      address_consignee: [],
    });
  }

  ngOnInit(): void {
    const parts = this.router.url.split("/");
    this.batchValue = Number(parts[parts.length - 1]);
    if (this.movimiento !== 'crear') {
      forkJoin([this.loteService.getTipoTransporte(), this.loteService.getTipoUnidad()])
      .subscribe({
        next: ([tipoTransporte, tipoUnidad]) => {
          this.tipoTransporte = this.sortArray<SalidaCatalogo>(tipoTransporte, 'description');
          this.tipoUnidad = this.sortArray<SalidaCatalogo>(tipoUnidad, 'description');
          if (this.loteService.granel) {
            this.outputForm.get('total_pieces')?.disable();
          }
        },
        error: (e) => {
          this.mensajeOverlay = 'Error en el servidor al cargar datos, inténtelo más tarde';
          this.iconoOverlay = 'bi bi-exclamation-circle-fill';
          this.buttons = false;
          this.overlay = true;
          setTimeout(() => {
            this.overlay = false;
          }, 3000);
        },
      });
    }
    if (this.movimiento === 'crear') {
      this.outputForm.get('ship_name')?.disable();
    }
    this.tipoSalida.valueChanges.subscribe( tipoSalida => {
      this.onTipoSalida.emit(tipoSalida);
    });
    this.output = {
      ...this.output,
      ...this.outputForm.value
    };
    if (this.router.url.includes('output-list')) {
      this.output.ship_name = this.outputDetail[0].ship_name;
      this.outputForm.reset({
        ...this.outputDetail[0],
        admission_date: this.outputDetail[0].departure_date.substring(0, 10),
      });
      this.outputForm.get('departure_date')?.setValue(this.outputDetail[0].departure_date.substring(0,10));
      if (this.outputDetail[0].id) {
        this.id = this.outputDetail[0].id;
      }
      this.outputForm.get('output_type_option_id')?.disable();
      this.outputForm.get('ship_name')?.disable();
      if (this.movimiento === 'detalle') {
        this.outputForm.disable();
      }
    }
    if (this.loteService.granel) {
      this.outputForm.get('total_pieces')?.disable();
    }
    this.outputForm.get('precinct_key')?.disable();
  }

  /**
   * Valida y envía el formulario para crear o actualizar la salida por barco del lote de cabotaje,
   * verificando los anexos y el tonelaje, y mostrando los mensajes de éxito o error.
   */
  saveOutput() {
    if (!this.authService.isValidPath('cabotage', 'outputs', 'create')) {
      return;
    }
    if (this.movimiento === 'crear') {
      const isExportCabotage = !this.router.url.includes('ship-list');
      if (!this.cabotageBatch[0].keys_files?.length && isExportCabotage) {
        this.mensajeOverlay = 'No se puede registrar salida porque el lote no tiene anexos';
        this.iconoOverlay = 'bi bi-info-circle-fill';
        this.buttons = false;
        this.overlay = true;
        setTimeout(() => {
          this.overlay = false;
        },4000)
        return;
      }
    }
    const invalidLocationsForm = this.amountsComponent.isInvalidForm();
    if (this.outputForm.invalid || invalidLocationsForm) {
      this.outputForm.markAllAsTouched();
      this.mensajeOverlay = 'Favor de verificar la información';
      this.iconoOverlay = 'bi bi-exclamation-circle-fill';
      this.buttons = false;
      this.overlay = true;
      setTimeout(() => {
        this.overlay = false;
      },2000);
      return;
    }
    if (Number(this.outputForm.get('total_tonnage')?.value) <= 0 && !this.outputForm.get('total_pieces')?.value) {
      this.mensajeOverlay = 'No es posible hacer un registro con tonelaje y número de piezas en 0';
      this.iconoOverlay = 'bi bi-exclamation-circle-fill';
      this.buttons = false;
      this.overlay = true;
      setTimeout(() => {
        this.overlay = false;
      },4000);
      return;
    }
    this.loading = true;
    this.output = {
      ...this.output,
      ...this.outputForm.value,
      cabotage_batch_id: this.batchValue,
      checker_name: this.outputForm.get('checker_name')?.value,
      output_type_option_id: Number(this.tipoSalida.value),
      total_tonnage: Number(this.outputForm.get('total_tonnage')?.value),
      // request_number: Number(this.outputForm.get('request_number')?.value),
      destination_id: Number(this.outputForm.get('destination_id')?.value),
      ship_number: Number(this.outputForm.get('ship_number')?.value),
      departure_date: this.outputForm.get('departure_date')?.value + ' 00:00:00',
      total_pieces: this.outputForm.get('total_pieces')?.value ? Number(this.outputForm.get('total_pieces')?.value) : 0,
    };
    if (this.movimiento === 'actualizar') {
      this.output = {
        ...this.output,
        id: this.outputDetail[0].id,
      };
      this.mensajeOverlay = 'La salida fue actualizada de manera correcta';
      this.iconoOverlay = 'bi bi-check2';
      this.buttons = false;
      this.loteCabotajeService.updateCabotageOutput(this.output)
      .subscribe({
        next: response => {
          if (response.error) {
            this.mensajeOverlay = response.message.includes('tonnage')
            ? 'La suma de toneladas es mayor a las toneladas de entrada'
            : response.message.includes('pieces')
            ? 'La suma de piezas es mayor a las piezas totales de entrada'
            : 'Lo sentimos no fue posible registrar la salida, inténtelo más tarde';
            this.iconoOverlay = 'bi bi-exclamation-circle-fill';
            this.buttons = false;
            this.loading = false;
            this.overlay = true;
            setTimeout(() => {
              this.overlay = false;
            }, 4000);
            return;
          }
          this.loteCabotajeService.getCabotageBatch(this.batchValue)
          .subscribe({
            next: batch => {
              this.loteCabotajeService.loteInfoCabotage = batch[0];
              const inputTons = batch[0].input_tons;
              const outputTons = Number(batch[0].output_tons?.toFixed(3));
              const twoPercent = inputTons ? inputTons * 0.02 : 0;
              let inputTons_twoPercent = Number(inputTons) + Number(twoPercent);
              inputTons_twoPercent = Number(inputTons_twoPercent.toFixed(3));
              this.loading = false;
              this.overlay = true;
              if (outputTons > inputTons_twoPercent) {
                setTimeout(() => {
                  this.mensajeOverlay = 'Tonelaje ha exedido mas del 2%';
                  this.iconoOverlay = 'bi bi-exclamation-circle-fill';
                  setTimeout(() => {
                    this.overlay = false;
                    this.regresar();
                  }, 3000);
                }, 2000);
                return;
              }
              setTimeout(() => {
                this.overlay = false;
                this.regresar();
              }, 2000);
            },
            error: (e) => {
              this.mensajeOverlay = 'Error en el servidor al actualizar el lote, inténtelo más tarde';
              this.iconoOverlay = 'bi bi-exclamation-circle-fill';
              this.buttons = false;
              this.loading = false;
              this.overlay = true;
              setTimeout(() => {
                this.overlay = false;
              }, 3000);
            },
          });
        },
        error: (e) => {
          this.mensajeOverlay = 'Error en el servidor al actualizar el registro, inténtelo más tarde';
          this.iconoOverlay = 'bi bi-exclamation-circle-fill';
          this.buttons = false;
          this.loading = false;
          this.overlay = true;
          setTimeout(() => {
            this.overlay = false;
          }, 3000);
        },
      });
      return;
    }
    this.mensajeOverlay = 'La salida fue agregada de manera correcta';
    this.iconoOverlay = 'bi bi-check2';
    this.buttons = false;
    this.loteCabotajeService.createCabotageOutput(this.output)
    .subscribe({
      next: response => {
        if (response.error) {
          this.mensajeOverlay = response.message.includes('tonnage')
          ? 'La suma de toneladas es mayor a las toneladas de entrada'
          : response.message.includes('pieces')
          ? 'La suma de piezas es mayor a las piezas totales de entrada'
          : 'Lo sentimos no fue posible registrar la salida, inténtelo más tarde';
          this.iconoOverlay = 'bi bi-exclamation-circle-fill';
          this.buttons = false;
          this.loading = false;
          this.overlay = true;
          setTimeout(() => {
            this.overlay = false;
          }, 4000);
          return;
        }
        this.loteCabotajeService.getCabotageBatch(this.batchValue)
        .subscribe({
          next: batch => {
            this.loteCabotajeService.loteInfoCabotage = batch[0];
            const inputTons = batch[0].input_tons;
            const outputTons = Number(batch[0].output_tons?.toFixed(3));
            const twoPercent = inputTons ? inputTons * 0.02 : 0;
            let inputTons_twoPercent = Number(inputTons) + Number(twoPercent);
            inputTons_twoPercent = Number(inputTons_twoPercent.toFixed(3));
            this.loading = false;
            this.overlay = true;
            if (outputTons > inputTons_twoPercent) {
              setTimeout(() => {
                this.mensajeOverlay = 'Tonelaje ha exedido mas del 2%';
                this.iconoOverlay = 'bi bi-exclamation-circle-fill';
                setTimeout(() => {
                  this.overlay = false;
                  if (this.router.url.includes('/ship-list')) {
                    this.router.navigate([`/ship-list/${this.blDetails.operation_number}/cabotage/batch-movement/output-list`, this.batchValue]);
                  } else {
                    this.router.navigate([`/cabotage/batch-movement/output-list`, this.batchValue])
                  }
                }, 3000);
              }, 2000);
              return;
            }
            setTimeout(() => {
              this.overlay = false;
              if (this.router.url.includes('/ship-list')) {
                this.router.navigate([`/ship-list/${this.blDetails.operation_number}/cabotage/batch-movement/output-list`, this.batchValue]);
              } else {
                this.router.navigate([`/cabotage/batch-movement/output-list`, this.batchValue])
              }
            }, 2000);
          },
          error: (e) => {
            this.mensajeOverlay = 'Error en el servidor al actualizar el lote, inténtelo más tarde';
            this.iconoOverlay = 'bi bi-exclamation-circle-fill';
            this.buttons = false;
            this.loading = false;
            this.overlay = true;
            setTimeout(() => {
              this.overlay = false;
            }, 3000);
          },
        });
      },
      error: (e) => {
        this.mensajeOverlay = 'Error en el servidor al crear el registro, inténtelo más tarde';
        this.iconoOverlay = 'bi bi-exclamation-circle-fill';
        this.buttons = false;
        this.loading = false;
        this.overlay = true;
        setTimeout(() => {
          this.overlay = false;
        }, 3000);
      },
    });
  }

  /**
   * Indica si un campo del formulario de salida es inválido y ha sido tocado por el usuario.
   * @param campo - Nombre del control del formulario a validar.
   * @returns `true` si el campo tiene errores y fue tocado; de lo contrario `null` o `false`.
   */
  campoEsValido( campo: string): boolean | null {
    return this.outputForm.get(campo)!.errors &&
    this.outputForm.get(campo)!.touched;
  }

  /**
   * Obtiene la fecha actual con formato `YYYY-MM-DD`.
   * @returns La fecha actual como cadena de texto.
   */
  getDate(): string {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const date = `${year}-${month}-${day}`;
    return date;
  }

  /**
   * Ordena un arreglo de manera ascendente según la clave indicada.
   * @param array - Arreglo de elementos a ordenar.
   * @param key - Clave del elemento por la cual se ordena.
   * @returns El arreglo ordenado.
   */
  sortArray<T>(array: T[], key: keyof T): T[] {
    return array = array.sort(function (a, b) {
      if (a[key] > b[key]) {
        return 1;
      }
      if (a[key] < b[key]) {
        return -1;
      }
      return 0;
    });
  }

  /**
   * Procesa el archivo seleccionado para un campo del formulario, validando su tamaño
   * y convirtiéndolo a base64.
   * @param event - Evento del input de tipo archivo.
   * @param campo - Nombre del control del formulario donde se asigna el archivo.
   */
  fileInput(event: any, campo: string): void {
    if (event.target.files[0]) {
      if (event.target.files[0].size > 15 * 1024 * 1024) {
        switch (campo) {
          case 'ticket_document':
            this.mensajeError = 'El archivo tiene que ser menos a 15mb';
            this.maxSize = true;
            break;
        }
        this.outputForm.get(campo)?.setValue('');
        return;
      }
      this.maxSize = false;
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const cadena = reader.result?.toString() || '';
        const contenidoBase64 = cadena.split(',')[1];
        this.outputForm.get(campo)?.setValue(contenidoBase64);
      }
    } else {
      switch (campo) {
        case 'ticket_document':
          this.mensajeError = 'Ingrese el archivo';
          break;
      }
      this.outputForm.get(campo)?.setValue('');
    }
  }

  /**
   * Asigna el nombre de la embarcación seleccionada a partir del número de barco capturado.
   */
  changeBoat() {
    if (this.outputForm.get('ship_number')?.value) {
      const boat = this.boats.find( boat => boat.boats_number == Number(this.outputForm.get('ship_number')?.value));
      this.output.ship_name = boat?.boats_name ? boat.boats_name : '';
      this.outputForm.get('ship_name')?.setValue(boat?.boats_name);
    } else {
      this.outputForm.get('ship_name')?.setValue('');
    }
  }

  /**
   * Emite el evento para regresar a la vista anterior del flujo de salidas.
   */
  regresar() {
    this.onRegresar.emit(true);
  }
}
